import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  FireIcon, 
  ChevronDownIcon,
  CheckIcon,
  ArrowLeftIcon,
  InformationCircleIcon,
  XMarkIcon,
  PlayIcon
} from '@heroicons/react/24/outline';
import { api } from '../../services/api';

interface ExerciseSet {
  setNumber: number;
  targetReps: number;
  targetWeight?: string;
  targetDuration?: number;
  targetDistance?: number;
  weightUnit?: 'lbs' | 'kg';
  eachSide?: boolean;
  // Client tracking fields
  actualReps?: number;
  actualWeight?: string;
  actualDuration?: number;
  actualTempo?: string;
  notes?: string;
}

interface WorkoutExercise {
  exerciseId: string;
  exerciseName: string;
  order: number;
  equipment?: string[];
  duration?: string;
  tempo?: string;
  description?: string;
  specialInstructions?: string;
  videoUrl?: string;
  notes?: string;
  completedAsPrescribed?: boolean;
  // Duration-based exercise tracking
  actualDuration?: string;
  actualTempo?: string;
  sets: ExerciseSet[];
}

interface WorkoutSet {
  setNumber: number;
  setType?: string;
  restPeriod?: string;
  specialInstructions?: string;
  intensityLevel?: number;
  targetHeartRate?: number;
  description?: string;
  exercises: WorkoutExercise[];
}

interface Workout {
  _id: string;
  name: string;
  description?: string;
  status: string;
  scheduledDate?: string;
  completedDate?: string;
  estimatedDuration?: number;
  sets: WorkoutSet[];
  overallFeedback?: string;
}

// Helper functions for exercise types
const hasValidSets = (workoutExercise: WorkoutExercise) => {
  return workoutExercise.sets && workoutExercise.sets.length > 0 && 
         workoutExercise.sets.some(set => set.targetReps > 0);
};

const hasDurationOrTempo = (workoutExercise: WorkoutExercise) => {
  return (workoutExercise.duration && workoutExercise.duration.trim()) || 
         (workoutExercise.tempo && workoutExercise.tempo.trim());
};

const isDurationOnlyExercise = (workoutExercise: WorkoutExercise) => {
  return !hasValidSets(workoutExercise) && hasDurationOrTempo(workoutExercise);
};

const WorkoutDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // State
  const [expandedExercises, setExpandedExercises] = useState<Map<number, Set<number>>>(new Map());

  const [workoutData, setWorkoutData] = useState<Workout | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState<Map<string, boolean>>(new Map());
  const [showDescriptionModal, setShowDescriptionModal] = useState(false);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [selectedExercise, setSelectedExercise] = useState<WorkoutExercise | null>(null);

  // Fetch workout data
  const { data: workout, isLoading } = useQuery({
    queryKey: ['workout', id],
    queryFn: () => api.get(`/workouts/${id}`),
    enabled: !!id,
  });

  // Initialize workout data when loaded
  useEffect(() => {
    if (workout?.data) {
      // Initialize with defaults: all exercises "completed as assigned"
      const initializedWorkout = {
        ...workout.data,
        sets: workout.data.sets.map((set: WorkoutSet) => ({
          ...set,
          exercises: set.exercises.map((exercise: WorkoutExercise) => {
            const updatedExercise = {
              ...exercise,
              // Default to "completed as assigned" (true)
              completedAsPrescribed: exercise.completedAsPrescribed ?? true,
              sets: exercise.sets.map((exerciseSet: ExerciseSet) => ({
                ...exerciseSet,
                // Initialize actual values to target values (completed as assigned)
                actualReps: exerciseSet.actualReps ?? exerciseSet.targetReps,
                actualWeight: exerciseSet.actualWeight ?? exerciseSet.targetWeight,
                actualDuration: exerciseSet.actualDuration ?? exerciseSet.targetDuration
              }))
            };
            
            // For duration-only exercises, also initialize exercise-level actual values
            if (isDurationOnlyExercise(updatedExercise)) {
              updatedExercise.actualDuration = updatedExercise.actualDuration ?? updatedExercise.duration;
              updatedExercise.actualTempo = updatedExercise.actualTempo ?? updatedExercise.tempo;
            }
            
            return updatedExercise;
          })
        }))
      };
      
      setWorkoutData(initializedWorkout);
      
      // Initialize expanded exercises map
      setExpandedExercises(new Map());
    }
  }, [workout]);

  // Helper function to generate exercise key
  const getExerciseKey = (setIndex: number, exerciseIndex: number) => {
    return `${setIndex}-${exerciseIndex}`;
  };

  // Helper function to set unsaved changes for specific exercise
  const setExerciseUnsavedChanges = (setIndex: number, exerciseIndex: number, hasChanges: boolean) => {
    const key = getExerciseKey(setIndex, exerciseIndex);
    setHasUnsavedChanges(prev => new Map(prev.set(key, hasChanges)));
  };

  // Helper function to validate if exercise has valid data to submit
  const hasValidExerciseData = (setIndex: number, exerciseIndex: number) => {
    if (!workoutData) return false;
    
    const exercise = workoutData.sets[setIndex].exercises[exerciseIndex];
    
    // Check if there's feedback/notes
    if (exercise.notes && exercise.notes.trim()) {
      return true;
    }
    
    // Check if exercise is in modify mode and has valid actual values
    if (!exercise.completedAsPrescribed) {
      // For exercises with sets
      if (hasValidSets(exercise)) {
        // ALL sets must have actual reps populated
        return exercise.sets.every(set => 
          (set.actualReps !== undefined && set.actualReps !== null && set.actualReps > 0) &&
          (set.actualWeight !== undefined && set.actualWeight !== null && set.actualWeight.trim() !== '')
        );
      }
      
      // For duration-only exercises
      if (isDurationOnlyExercise(exercise)) {
        return (exercise.actualDuration && exercise.actualDuration.trim()) ||
               (exercise.actualTempo && exercise.actualTempo.trim());
      }
    }
    
    return false;
  };

  // Accordion management
  const toggleExerciseExpanded = (setIndex: number, exerciseIndex: number) => {
    const newExpandedExercises = new Map(expandedExercises);
    const setExercises = new Set(newExpandedExercises.get(setIndex) || []);
    
    if (setExercises.has(exerciseIndex)) {
      setExercises.delete(exerciseIndex);
    } else {
      setExercises.add(exerciseIndex);
    }
    
    newExpandedExercises.set(setIndex, setExercises);
    setExpandedExercises(newExpandedExercises);
  };



  // Update exercise set data
  const updateExerciseSet = (setIndex: number, exerciseIndex: number, setIdx: number, field: string, value: any) => {
    if (!workoutData) return;
    
    const updatedWorkout = { ...workoutData };
    const exerciseSet = updatedWorkout.sets[setIndex].exercises[exerciseIndex].sets[setIdx];
    (exerciseSet as any)[field] = value;
    
    setWorkoutData(updatedWorkout);
    
    // Check if exercise has valid data after this change
    setTimeout(() => {
      const hasValidData = hasValidExerciseData(setIndex, exerciseIndex);
      setExerciseUnsavedChanges(setIndex, exerciseIndex, !!hasValidData);
    }, 0);
  };

  // Update exercise-level data (for duration-based exercises)
  const updateExercise = (setIndex: number, exerciseIndex: number, field: string, value: any) => {
    if (!workoutData) return;
    
    const updatedWorkout = { ...workoutData };
    const exercise = updatedWorkout.sets[setIndex].exercises[exerciseIndex];
    (exercise as any)[field] = value;
    
    setWorkoutData(updatedWorkout);
    
    // Check if exercise has valid data after this change
    setTimeout(() => {
      const hasValidData = hasValidExerciseData(setIndex, exerciseIndex);
      setExerciseUnsavedChanges(setIndex, exerciseIndex, !!hasValidData);
    }, 0);
  };

  // Set exercise completion mode (either/or: completed as assigned OR modify actual)
  const setExerciseCompletionMode = (setIndex: number, exerciseIndex: number, mode: 'assigned' | 'modify') => {
    if (!workoutData) return;
    
    const updatedWorkout = { ...workoutData };
    const exercise = updatedWorkout.sets[setIndex].exercises[exerciseIndex];
    
    exercise.completedAsPrescribed = mode === 'assigned';
    
    // Apply to all sets in this exercise (if exercise has valid sets)
    if (hasValidSets(exercise)) {
      exercise.sets.forEach((exerciseSet) => {
        if (mode === 'assigned') {
          // When "completed as assigned", copy target values to actual values
          exerciseSet.actualReps = exerciseSet.targetReps;
          exerciseSet.actualWeight = exerciseSet.targetWeight;
          exerciseSet.actualDuration = exerciseSet.targetDuration;
          exerciseSet.actualTempo = undefined; // Tempo comes from exercise level, not set level
        } else {
          // When "modify actual", clear actual values so user can enter them
          exerciseSet.actualReps = undefined;
          exerciseSet.actualWeight = undefined;
          exerciseSet.actualDuration = undefined;
          exerciseSet.actualTempo = undefined;
        }
      });
    }
    
    // Also handle exercise-level duration/tempo fields
    if (isDurationOnlyExercise(exercise)) {
      if (mode === 'assigned') {
        // When "completed as assigned", copy target to actual
        exercise.actualDuration = exercise.duration;
        exercise.actualTempo = exercise.tempo;
      } else {
        // When "modify actual", clear actual values
        exercise.actualDuration = undefined;
        exercise.actualTempo = undefined;
      }
    }
    
    setWorkoutData(updatedWorkout);
    
    // Only set unsaved changes if switching to 'modify' mode
    if (mode === 'modify') {
      setExerciseUnsavedChanges(setIndex, exerciseIndex, true);
    } else {
      // Clear unsaved changes when switching back to 'assigned'
      setExerciseUnsavedChanges(setIndex, exerciseIndex, false);
    }
  };



  // Save workout completion
  const saveWorkoutCompletion = useMutation({
    mutationFn: async () => {
      if (!workoutData) throw new Error('No workout data');
      
      return api.patch(`/workouts/${id}/complete`, {
        sets: workoutData.sets,
        status: 'COMPLETED',
        completedDate: new Date().toISOString()
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workouts'] });
      queryClient.invalidateQueries({ queryKey: ['workout', id] });
      navigate('/client/workouts');
    },
    onError: (error: any) => {
      setError(error.response?.data?.error || error.message || 'Failed to save workout');
    }
  });

  const handleCompleteWorkout = async () => {
    setIsSubmitting(true);
    setError(null);
    
    try {
      await saveWorkoutCompletion.mutateAsync();
    } catch (error) {
      console.error('Error completing workout:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Submit performance/feedback without completing workout
  const submitPerformanceFeedback = useMutation({
    mutationFn: async () => {
      if (!workoutData) throw new Error('No workout data');
      
      return api.patch(`/workouts/${id}`, {
        sets: workoutData.sets
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workout', id] });
      setHasUnsavedChanges(new Map());
    },
    onError: (error: any) => {
      setError(error.response?.data?.error || error.message || 'Failed to save performance feedback');
    }
  });

  const handleSubmitFeedback = async () => {
    setIsSubmittingFeedback(true);
    setError(null);
    
    try {
      await submitPerformanceFeedback.mutateAsync();
    } catch (error) {
      console.error('Error submitting feedback:', error);
    } finally {
      setIsSubmittingFeedback(false);
    }
  };

  const isWorkoutCompleted = workoutData?.status === 'COMPLETED';

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading workout...</p>
        </div>
      </div>
    );
  }

  if (!workout?.data) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">Workout not found</p>
          <button
            onClick={() => navigate('/client/workouts')}
            className="text-blue-600 hover:text-blue-800"
          >
            Back to Workouts
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 -mt-6">
      {/* Description Modal */}
      {showDescriptionModal && selectedExercise && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">{selectedExercise.exerciseName}</h3>
                <button
                  onClick={() => {
                    setShowDescriptionModal(false);
                    setSelectedExercise(null);
                  }}
                  className="text-gray-600 hover:text-gray-900"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
              <div className="prose prose-sm max-w-none">
                <p className="text-gray-700 whitespace-pre-wrap">
                  {selectedExercise.description || 'No description available for this exercise.'}
                </p>
              </div>
              {selectedExercise.specialInstructions && (
                <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                  <h4 className="font-medium text-blue-900 mb-2">Special Instructions</h4>
                  <p className="text-blue-800 text-sm whitespace-pre-wrap">
                    {selectedExercise.specialInstructions}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Video Modal */}
      {showVideoModal && selectedExercise && selectedExercise.videoUrl && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-75 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden">
            <div className="p-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">{selectedExercise.exerciseName} - Demo</h3>
                <button
                  onClick={() => {
                    setShowVideoModal(false);
                    setSelectedExercise(null);
                  }}
                  className="text-gray-600 hover:text-gray-900"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
            </div>
            <div className="p-4">
              <div className="aspect-video w-full">
                {selectedExercise.videoUrl.includes('youtube.com') || selectedExercise.videoUrl.includes('youtu.be') ? (
                  <iframe
                    className="w-full h-full rounded"
                    src={selectedExercise.videoUrl.replace('watch?v=', 'embed/').replace('youtu.be/', 'youtube.com/embed/')}
                    title={`${selectedExercise.exerciseName} demonstration`}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                ) : selectedExercise.videoUrl.includes('vimeo.com') ? (
                  <iframe
                    className="w-full h-full rounded"
                    src={selectedExercise.videoUrl.replace('vimeo.com/', 'player.vimeo.com/video/')}
                    title={`${selectedExercise.exerciseName} demonstration`}
                    frameBorder="0"
                    allow="autoplay; fullscreen; picture-in-picture"
                    allowFullScreen
                  />
                ) : (
                  <video
                    className="w-full h-full rounded"
                    controls
                    preload="metadata"
                  >
                    <source src={selectedExercise.videoUrl} type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>
                )}
              </div>
              {selectedExercise.description && (
                <div className="mt-4 p-3 bg-gray-50 rounded">
                  <h4 className="font-medium text-gray-900 mb-2">Exercise Description</h4>
                  <p className="text-gray-700 text-sm whitespace-pre-wrap">
                    {selectedExercise.description}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Sticky Header */}
      <div className="sticky top-16 z-20 bg-white border-b border-gray-200 px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <button
              onClick={() => navigate('/client/workouts')}
              className="p-1 text-gray-600 hover:text-gray-900"
            >
              <ArrowLeftIcon className="h-5 w-5" />
            </button>
            <FireIcon className="h-5 w-5 text-blue-600" />
            <div>
              <h1 className="text-lg font-semibold text-gray-900">
                {workoutData?.name}
              </h1>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            {!isWorkoutCompleted && (
              <button
                onClick={handleCompleteWorkout}
                disabled={isSubmitting}
                className="flex items-center space-x-1 bg-green-600 text-white px-3 py-1.5 rounded-md hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-sm"
              >
                {isSubmitting ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                ) : (
                  <CheckIcon className="h-4 w-4" />
                )}
                <span>{isSubmitting ? 'Completing...' : 'Complete'}</span>
              </button>
            )}
            {isWorkoutCompleted && (
              <div className="flex items-center space-x-1 text-green-600 text-sm">
                <CheckIcon className="h-4 w-4" />
                <span>Completed</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="px-4 py-4 space-y-4">
        {/* Workout Info */}
        <div className="bg-white rounded-lg shadow-sm p-3 space-y-3">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <p className="text-gray-500">Scheduled Date</p>
              <p className="font-medium">
                {workoutData?.scheduledDate 
                  ? (() => {
                      const dateString = workoutData.scheduledDate;
                      let date: Date;
                      if (dateString.includes('T')) {
                        const datePart = dateString.split('T')[0];
                        date = new Date(datePart + 'T00:00:00');
                      } else {
                        date = new Date(dateString + 'T00:00:00');
                      }
                      return date.toLocaleDateString();
                    })()
                  : 'Not scheduled'
                }
              </p>
            </div>
            <div>
              <p className="text-gray-500">Estimated Duration</p>
              <p className="font-medium">
                {workoutData?.estimatedDuration 
                  ? `${workoutData.estimatedDuration} min`
                  : 'Not specified'
                }
              </p>
            </div>
          </div>
          {workoutData?.description && (
            <div>
              <p className="text-gray-500 text-sm mb-1">Description</p>
              <p className="text-sm">{workoutData.description}</p>
            </div>
          )}
          </div>

        {/* Workout Sets */}
        {workoutData?.sets && workoutData.sets.length > 0 && (
              <div className="space-y-2">
            {workoutData.sets.map((set, setIndex) => (
              <div key={setIndex} className="bg-white rounded-lg shadow-sm border border-gray-200">
                {/* Set Header */}
                <div className="p-2 border-b border-gray-100">
                  <h3 className="text-sm font-semibold text-gray-900">
                    Set {set.setNumber}
                    {set.setType && ` - ${set.setType}`}
                  </h3>
                </div>

                {/* Set Content */}
                <div className="p-2">
                    {/* Set Info */}
                    {(set.restPeriod || set.specialInstructions || set.intensityLevel || set.targetHeartRate) && (
                      <div className="mb-1 p-1 bg-gray-50 rounded-lg text-sm space-y-0">
                        {set.restPeriod && (
                          <p><span className="font-medium">Rest:</span> {set.restPeriod}</p>
                        )}
                        {set.intensityLevel && (
                          <p><span className="font-medium">Intensity:</span> {set.intensityLevel}/10</p>
                        )}
                        {set.targetHeartRate && (
                          <p><span className="font-medium">Target Heart Rate:</span> {set.targetHeartRate} bpm</p>
                        )}
                        {set.specialInstructions && (
                          <p><span className="font-medium">Instructions:</span> {set.specialInstructions}</p>
                        )}
                      </div>
                    )}

                    {/* Exercises */}
                    <div className="space-y-2">
                      {set.exercises.map((exercise, exerciseIndex) => (
                        <div key={exerciseIndex} className="border border-gray-200 rounded-lg">
                          {/* Exercise Header */}
                          <div 
                            className="flex items-center justify-between p-2 cursor-pointer hover:bg-gray-50"
                            onClick={() => toggleExerciseExpanded(setIndex, exerciseIndex)}
                          >
                            <div className="flex items-center space-x-2">
                              <ChevronDownIcon 
                                className={`h-4 w-4 text-gray-500 transition-transform ${
                                  expandedExercises.get(setIndex)?.has(exerciseIndex) ? 'rotate-180' : ''
                                }`} 
                              />
                              <h4 className="text-sm font-medium text-gray-900">{exercise.exerciseName}</h4>
                            </div>
                            <div className="text-xs text-gray-500">
                              {exercise.sets.length} set{exercise.sets.length !== 1 ? 's' : ''}
                            </div>
                          </div>

                          {/* Exercise Content */}
                          {expandedExercises.get(setIndex)?.has(exerciseIndex) && (
                            <div className="px-2 pb-2">
                              {/* Exercise Info */}
                              {(exercise.description || exercise.videoUrl || exercise.specialInstructions || (exercise.duration && exercise.duration.trim()) || (exercise.tempo && exercise.tempo.trim())) && (
                                <div className="mb-2 p-2 rounded text-sm space-y-1">
                                  <div className="flex space-x-4">
                                    {exercise.description && (
                                      <button
                                        onClick={() => {
                                          setSelectedExercise(exercise);
                                          setShowDescriptionModal(true);
                                        }}
                                        className="flex items-center space-x-1 text-blue-600 hover:text-blue-800 underline"
                                      >
                                        <InformationCircleIcon className="h-4 w-4" />
                                        <span>Description</span>
                                      </button>
                                    )}
                                    {exercise.videoUrl && (
                                      <button
                                        onClick={() => {
                                          setSelectedExercise(exercise);
                                          setShowVideoModal(true);
                                        }}
                                        className="flex items-center space-x-1 text-green-600 hover:text-green-800 underline"
                                      >
                                        <PlayIcon className="h-4 w-4" />
                                        <span>Watch Demo</span>
                                      </button>
                                    )}
                                  </div>
                                  {exercise.duration && exercise.duration.trim() && hasValidSets(exercise) && (
                                    <p><span className="font-medium">Duration:</span> {exercise.duration}</p>
                                  )}
                                  {exercise.tempo && exercise.tempo.trim() && hasValidSets(exercise) && (
                                    <p><span className="font-medium">Tempo:</span> {exercise.tempo}</p>
                                  )}
                                  {exercise.specialInstructions && (
                                    <p><span className="font-medium">Instructions:</span> {exercise.specialInstructions}</p>
                                  )}
                                </div>
                              )}

                              {/* Exercise Performance - Sets vs Duration */}
                              {hasValidSets(exercise) ? (
                                // Traditional sets interface
                                <div className="space-y-2 mb-3">
                                  <label className="block text-xs font-medium text-gray-700">
                                    Sets & Reps
                                  </label>
                                  {exercise.sets.map((exerciseSet, setIdx) => (
                                  <div key={setIdx} className="space-y-2">
                                    {/* Labels for first set only */}
                                    {setIdx === 0 && (
                                      <div className="flex items-center space-x-2">
                                        <div className="w-12 flex justify-center">
                                          <span className="text-xs text-gray-500">Set</span>
                                        </div>
                                        <div className="w-16 flex justify-center">
                                          <span className="text-xs text-gray-500">Reps</span>
                                        </div>
                                        {!exercise.completedAsPrescribed && (
                                          <div className="w-16 flex justify-center">
                                            <span className="text-xs text-gray-500">Actual</span>
                                          </div>
                                        )}
                                        <div className="w-16 flex justify-center">
                                          <span className="text-xs text-gray-500">Weight</span>
                                        </div>
                                        {!exercise.completedAsPrescribed && (
                                          <div className="w-16 flex justify-center">
                                            <span className="text-xs text-gray-500">Actual</span>
                                          </div>
                                        )}
                                        <div className="w-24 flex justify-center">
                                          <span className="text-xs text-gray-500">Units</span>
                                        </div>
                                      </div>
                                    )}
                                    
                                    {/* Set row with aligned inputs */}
                                    <div className="flex items-end space-x-2">
                                      {/* Set number */}
                                      <div className="w-12 flex justify-center">
                                        <span className="text-sm font-medium text-gray-600">{exerciseSet.setNumber}</span>
                                      </div>
                                      
                                      {/* Target Reps (read-only) */}
                                      <div className="w-16">
                                        <div className="w-full px-2 py-1 border rounded text-sm text-center bg-gray-50 border-gray-200">
                                          {exerciseSet.targetReps}
                                        </div>
                                      </div>
                                      
                                      {/* Actual Reps input - only show if NOT completed as prescribed */}
                                      {!exercise.completedAsPrescribed && (
                                        <div className="w-16">
                                          <input
                                            type="number"
                                            min="0"
                                            value={exerciseSet.actualReps || ''}
                                            onChange={(e) => updateExerciseSet(setIndex, exerciseIndex, setIdx, 'actualReps', parseInt(e.target.value) || 0)}
                                            disabled={isWorkoutCompleted}
                                            className={`w-full px-2 py-1 border rounded text-sm text-center ${
                                              isWorkoutCompleted ? 'bg-gray-100 cursor-not-allowed' : 'border-gray-300'
                                            }`}
                                          />
                                        </div>
                                      )}
                                      
                                      {/* Target Weight (read-only) */}
                                      <div className="w-16">
                                        <div className="w-full px-2 py-1 border rounded text-sm text-center bg-gray-50 border-gray-200">
                                          {exerciseSet.targetWeight || '-'}
                                        </div>
                                      </div>

                                      {/* Actual Weight input - only show if NOT completed as prescribed */}
                                      {!exercise.completedAsPrescribed && (
                                        <div className="w-16">
                                          <input
                                            type="text"
                                            value={exerciseSet.actualWeight || ''}
                                            onChange={(e) => updateExerciseSet(setIndex, exerciseIndex, setIdx, 'actualWeight', e.target.value)}
                                            disabled={isWorkoutCompleted}
                                            className={`w-full px-2 py-1 border rounded text-sm text-center ${
                                              isWorkoutCompleted ? 'bg-gray-100 cursor-not-allowed' : 'border-gray-300'
                                            }`}
                                          />
                                        </div>
                                      )}
                                     
                                      {/* Unit display */}
                                      <div className="w-24 flex justify-center">
                                        <div className="flex space-x-1">
                                          {exerciseSet.weightUnit && (
                                            <div className="px-1 text-xs text-blue-600">
                                              {exerciseSet.weightUnit}
                                            </div>
                                          )}
                                          {exerciseSet.eachSide && (
                                            <div className="px-1 text-xs text-green-600" title="Weight per side">
                                              ea
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                  ))}
                                </div>
                              ) : (
                                // Duration-based exercise interface (table format like sets)
                                <div className="space-y-2 mb-3">
                                  <label className="block text-xs font-medium text-gray-700">
                                    Exercise Performance
                                  </label>
                                  
                                  <div className="overflow-x-auto">
                                    <table className="w-full text-xs border border-gray-200 rounded">
                                      <thead>
                                        <tr className="bg-gray-50">
                                          {exercise.duration && exercise.duration.trim() && (
                                            <th className="px-2 py-1 text-left font-medium text-gray-700 border-r border-gray-200">Duration</th>
                                          )}
                                          {exercise.tempo && exercise.tempo.trim() && (
                                            <th className="px-2 py-1 text-left font-medium text-gray-700">Pace/Intensity</th>
                                          )}
                                        </tr>
                                      </thead>
                                      <tbody>
                                        <tr className="border-t border-gray-200">
                                          {exercise.duration && exercise.duration.trim() && (
                                            <td className="px-2 py-2 border-r border-gray-200 font-medium">
                                              {!isWorkoutCompleted && !exercise.completedAsPrescribed ? (
                                                <input 
                                                  type="text"
                                                  value={exercise.actualDuration || ''}
                                                  onChange={(e) => updateExercise(setIndex, exerciseIndex, 'actualDuration', e.target.value)}
                                                  className="w-full px-1 py-0.5 border border-gray-300 rounded text-xs"
                                                />
                                              ) : (
                                                exercise.duration
                                              )}
                                            </td>
                                          )}
                                          {exercise.tempo && exercise.tempo.trim() && (
                                            <td className="px-2 py-2 font-medium">
                                              {!isWorkoutCompleted && !exercise.completedAsPrescribed ? (
                                                <input 
                                                  type="text"
                                                  value={exercise.actualTempo || ''}
                                                  onChange={(e) => updateExercise(setIndex, exerciseIndex, 'actualTempo', e.target.value)}
                                                  className="w-full px-1 py-0.5 border border-gray-300 rounded text-xs"
                                                />
                                              ) : (
                                                exercise.tempo
                                              )}
                                            </td>
                                          )}
                                        </tr>
                                      </tbody>
                                    </table>
                                  </div>
                                  
                                  {/* Notes section */}
                                  {!isWorkoutCompleted && !exercise.completedAsPrescribed && (
                                    <div className="mt-2">
                                      <label className="block text-xs font-medium text-gray-700 mb-1">
                                        Notes
                                      </label>
                                      <textarea 
                                        value={exercise.notes || ''}
                                        onChange={(e) => updateExercise(setIndex, exerciseIndex, 'notes', e.target.value)}
                                        className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                                        rows={2}
                                      />
                                    </div>
                                  )}
                                  
                                  {/* Show completed values for finished workouts */}
                                  {isWorkoutCompleted && (exercise.actualDuration || exercise.actualTempo || exercise.notes) && (
                                    <div className="mt-2 p-3 bg-green-50 rounded">
                                      <h4 className="text-xs font-medium text-green-800 mb-2">Completed Performance</h4>
                                      <div className="space-y-1">
                                        {exercise.actualDuration && (
                                          <div className="flex">
                                            <span className="text-xs text-green-600 w-20">Duration:</span>
                                            <span className="text-xs font-medium text-green-800">{exercise.actualDuration}</span>
                                          </div>
                                        )}
                                        {exercise.actualTempo && (
                                          <div className="flex">
                                            <span className="text-xs text-green-600 w-20">Pace:</span>
                                            <span className="text-xs font-medium text-green-800">{exercise.actualTempo}</span>
                                          </div>
                                        )}
                                        {exercise.notes && (
                                          <div className="flex">
                                            <span className="text-xs text-green-600 w-20">Notes:</span>
                                            <span className="text-xs text-green-700">{exercise.notes}</span>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              )}

                              {/* Exercise Completion Mode - Show for both sets and duration exercises */}
                              {(hasValidSets(exercise) || isDurationOnlyExercise(exercise)) && (
                                <div className="mb-3">
                                <div className="space-y-2">
                                  <label className="flex items-center space-x-2 cursor-pointer">
                                    <input
                                      type="radio"
                                      name={`completion-mode-${setIndex}-${exerciseIndex}`}
                                      checked={exercise.completedAsPrescribed === true}
                                      onChange={() => setExerciseCompletionMode(setIndex, exerciseIndex, 'assigned')}
                                      disabled={isWorkoutCompleted}
                                      className="h-4 w-4 text-green-600 border-gray-300 focus:ring-green-500"
                                    />
                                    <span className={`text-sm ${
                                      exercise.completedAsPrescribed === true
                                        ? 'text-green-700 font-medium' 
                                        : 'text-gray-700'
                                    }`}>
                                      Completed as assigned
                                    </span>
                                  </label>
                                  
                                  <label className="flex items-center space-x-2 cursor-pointer">
                                    <input
                                      type="radio"
                                      name={`completion-mode-${setIndex}-${exerciseIndex}`}
                                      checked={exercise.completedAsPrescribed === false}
                                      onChange={() => setExerciseCompletionMode(setIndex, exerciseIndex, 'modify')}
                                      disabled={isWorkoutCompleted}
                                      className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                                    />
                                    <span className={`text-sm ${
                                      exercise.completedAsPrescribed === false
                                        ? 'text-blue-700 font-medium' 
                                        : 'text-gray-700'
                                    }`}>
                                      Modify actual performance
                        </span>
                                  </label>
                                </div>
                                </div>
                              )}


                              {/* Notes section for client feedback */}
                              <div className="mt-3">
                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                  Notes / Feedback
                                </label>
                                <input
                                  type="text"
                                  value={exercise.notes || ''}
                                  onChange={(e) => {
                                    if (!workoutData) return;
                                    const updatedWorkout = { ...workoutData };
                                    updatedWorkout.sets[setIndex].exercises[exerciseIndex].notes = e.target.value;
                                    setWorkoutData(updatedWorkout);
                                    
                                    // Check if exercise has valid data after this change
                                    setTimeout(() => {
                                      const hasValidData = hasValidExerciseData(setIndex, exerciseIndex);
                                      setExerciseUnsavedChanges(setIndex, exerciseIndex, !!hasValidData);
                                    }, 0);
                                  }}
                                  disabled={isWorkoutCompleted}
                                  className={`w-full px-2 py-1 border rounded text-sm ${
                                    isWorkoutCompleted ? 'bg-gray-100 cursor-not-allowed' : 'border-gray-300'
                                  }`}
                                />
                              </div>
                              
                              {/* Submit Performance/Feedback Button for this exercise */}
                              {hasUnsavedChanges.get(getExerciseKey(setIndex, exerciseIndex)) && hasValidExerciseData(setIndex, exerciseIndex) && !isWorkoutCompleted && (
                                <div className="mt-2">
                                  <button
                                    onClick={handleSubmitFeedback}
                                    disabled={isSubmittingFeedback}
                                    className="w-full flex items-center justify-center space-x-2 bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-sm"
                                  >
                                    {isSubmittingFeedback ? (
                                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                                    ) : (
                                      <CheckIcon className="h-4 w-4" />
                                    )}
                                    <span>{isSubmittingFeedback ? 'Saving...' : 'Submit Performance/Feedback'}</span>
                                  </button>
                                </div>
                              )}
                            </div>
                          )}
                  </div>
                ))}
              </div>
            </div>
          </div>
            ))}
          </div>
        )}

        {/* Error Display */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Error saving workout</h3>
                <div className="mt-2 text-sm text-red-700">{error}</div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WorkoutDetail;
